export interface Case {
  id: string
  user_id: string
  case_number: string | null
  title: string
  case_type: 'civil_litigation' | 'trademark_admin' | 'other'
  party_role: 'plaintiff' | 'defendant' | 'third_party'
  opposing_party: string
  court: string | null
  claim_amount: number | null
  status: 'pending' | 'active' | 'closed' | 'suspended'
  notes: string | null
  filing_date: string | null
  deleted_at: string | null
  created_at: string
  updated_at: string
}

export interface Document {
  id: string
  case_id: string
  doc_type: string
  doc_date: string | null
  received_date: string | null
  source: string | null
  content: string | null
  file_url: string | null
  ocr_text: string | null
  ocr_confidence: number | null
  metadata: Record<string, unknown>
  created_at: string
}

export interface Node {
  id: string
  case_id: string
  node_name: string
  duration_days: number
  start_date: string
  end_date: string
  applicable_role: string | null
  trigger_doc_id: string | null
  status: 'upcoming' | 'active' | 'expiring' | 'expired' | 'completed'
  action_required: string | null
  priority: 'low' | 'medium' | 'high' | 'critical'
  notes: string | null
  completed_at: string | null
  created_at: string
  updated_at: string
}

export interface TimelineEvent {
  id: string
  case_id: string
  event_date: string
  event_type: string
  title: string
  description: string | null
  related_doc_id: string | null
  related_node_id: string | null
  created_at: string
}

export interface AnalysisReport {
  id: string
  case_id: string
  perspective: 'plaintiff' | 'defendant' | 'third_party'
  content: string
  checklist_status: Record<string, boolean>
  created_at: string
  updated_at: string
}

export interface Payment {
  id: string
  case_id: string
  judgment_id: string | null
  amount: number
  payment_date: string | null
  payment_method: string | null
  payer_name: string | null
  remark: string | null
  created_at: string
}

export interface Judgment {
  id: string
  case_id: string
  document_id: string | null
  judgment_type: '判决' | '调解' | '裁定'
  effective_date: string | null
  payable_amount: number | null
  payee_role: string | null
  created_at: string
  updated_at: string
}

export interface ReminderConfig {
  id: string
  user_id: string
  webhook_url: string
  secret_key: string
  enabled: boolean
  daily_summary_time: string
  created_at: string
  updated_at: string
}

export interface Notification {
  id: string
  user_id: string
  node_id: string | null
  case_id: string | null
  type: 'deadline_reminder' | 'overdue' | 'new_document'
  title: string
  body: string
  read: boolean
  priority?: 'low' | 'medium' | 'high' | 'critical'
  created_at: string
}

// 程序节点模板（用于自动推断）
export interface NodeTemplate {
  name: string
  durationDays: number
  applicableRole: 'plaintiff' | 'defendant' | 'third_party' | 'all'
  priority: 'low' | 'medium' | 'high' | 'critical'
  actionRequired: string
  triggerCondition: string  // 触发条件描述
}

// 民事诉讼节点模板
export const CIVIL_LITIGATION_NODES: NodeTemplate[] = [
  {
    name: '法院审查立案',
    durationDays: 7,
    applicableRole: 'all',
    priority: 'medium',
    actionRequired: '法院审查是否立案',
    triggerCondition: '提交起诉状',
  },
  {
    name: '送达被告',
    durationDays: 5,
    applicableRole: 'all',
    priority: 'medium',
    actionRequired: '法院向被告送达起诉状副本',
    triggerCondition: '立案完成',
  },
  {
    name: '答辩期',
    durationDays: 15,
    applicableRole: 'defendant',
    priority: 'critical',
    actionRequired: '被告提交答辩状或申请延期',
    triggerCondition: '被告收到起诉状副本',
  },
  {
    name: '举证期（普通程序）',
    durationDays: 15,
    applicableRole: 'all',
    priority: 'high',
    actionRequired: '提交证据及证据目录',
    triggerCondition: '收到应诉通知',
  },
  {
    name: '举证期（简易程序）',
    durationDays: 7,
    applicableRole: 'all',
    priority: 'high',
    actionRequired: '提交证据及证据目录',
    triggerCondition: '收到应诉通知（简易程序）',
  },
  {
    name: '诉讼费缴纳',
    durationDays: 7,
    applicableRole: 'plaintiff',
    priority: 'critical',
    actionRequired: '原告预交案件受理费',
    triggerCondition: '收到缴费通知',
  },
  {
    name: '管辖权异议期',
    durationDays: 15,
    applicableRole: 'defendant',
    priority: 'medium',
    actionRequired: '如有管辖权异议，在此期限内提出',
    triggerCondition: '被告收到起诉状副本',
  },
  {
    name: '反诉期',
    durationDays: 15,
    applicableRole: 'defendant',
    priority: 'medium',
    actionRequired: '被告如欲反诉，在此期限内提交反诉状',
    triggerCondition: '被告收到起诉状副本',
  },
  {
    name: '开庭审理',
    durationDays: 0,
    applicableRole: 'all',
    priority: 'high',
    actionRequired: '准备庭审材料、代理词、质证意见',
    triggerCondition: '法院通知开庭',
  },
  {
    name: '上诉期（判决）',
    durationDays: 15,
    applicableRole: 'all',
    priority: 'critical',
    actionRequired: '如不服判决，提交上诉状',
    triggerCondition: '收到判决书',
  },
  {
    name: '上诉期（裁定）',
    durationDays: 10,
    applicableRole: 'all',
    priority: 'critical',
    actionRequired: '如不服裁定，提交上诉状',
    triggerCondition: '收到裁定书',
  },
  {
    name: '上诉费缴纳',
    durationDays: 7,
    applicableRole: 'plaintiff',
    priority: 'critical',
    actionRequired: '上诉人预交上诉费',
    triggerCondition: '递交上诉状后收到缴费通知',
  },
  {
    name: '二审举证期',
    durationDays: 15,
    applicableRole: 'defendant',
    priority: 'high',
    actionRequired: '被上诉人提交反驳证据',
    triggerCondition: '收到上诉状',
  },
  {
    name: '申请执行',
    durationDays: 730,
    applicableRole: 'plaintiff',
    priority: 'high',
    actionRequired: '胜诉方在判决生效后2年内申请强制执行',
    triggerCondition: '判决/调解书生效',
  },
]

// 商标行政诉讼节点模板
export const TRADEMARK_ADMIN_NODES: NodeTemplate[] = [
  {
    name: '起诉期限',
    durationDays: 15,
    applicableRole: 'plaintiff',
    priority: 'critical',
    actionRequired: '收到商评委决定后15日内向北京知识产权法院起诉',
    triggerCondition: '收到商评委决定书/裁定书',
  },
  {
    name: '送达被告',
    durationDays: 5,
    applicableRole: 'all',
    priority: 'medium',
    actionRequired: '法院向被告（国家知识产权局）送达起诉状',
    triggerCondition: '立案完成',
  },
  {
    name: '答辩期',
    durationDays: 15,
    applicableRole: 'defendant',
    priority: 'high',
    actionRequired: '被告（国知局）提交答辩状',
    triggerCondition: '被告收到起诉状副本',
  },
  {
    name: '举证期',
    durationDays: 15,
    applicableRole: 'defendant',
    priority: 'critical',
    actionRequired: '被告提交作出行政决定的全部证据和依据（举证责任倒置）',
    triggerCondition: '收到应诉通知',
  },
  {
    name: '诉讼费缴纳',
    durationDays: 7,
    applicableRole: 'plaintiff',
    priority: 'critical',
    actionRequired: '原告预交案件受理费',
    triggerCondition: '收到缴费通知',
  },
  {
    name: '一审开庭',
    durationDays: 0,
    applicableRole: 'all',
    priority: 'high',
    actionRequired: '准备庭审材料',
    triggerCondition: '法院通知开庭',
  },
  {
    name: '一审判决',
    durationDays: 0,
    applicableRole: 'all',
    priority: 'high',
    actionRequired: '接收一审判决',
    triggerCondition: '法院作出判决',
  },
  {
    name: '上诉期（判决）',
    durationDays: 15,
    applicableRole: 'all',
    priority: 'critical',
    actionRequired: '如不服判决，向北京高院提起上诉',
    triggerCondition: '收到一审判决书',
  },
  {
    name: '二审开庭',
    durationDays: 0,
    applicableRole: 'all',
    priority: 'high',
    actionRequired: '准备二审庭审材料',
    triggerCondition: '二审法院通知开庭',
  },
  {
    name: '二审判决（终审）',
    durationDays: 0,
    applicableRole: 'all',
    priority: 'high',
    actionRequired: '接收二审判决',
    triggerCondition: '北京高院作出终审判决',
  },
]
